import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';

import { logArticleVideoPlay } from '../../../bi';
import { biLogger } from '../../../bi/biLogger';
import { createArticleSolutionInfo } from '../../../bi/solutionInfo';
import { getSharedBiFieldsFromState } from '../../../bi/utils/sharedBiFields';
import { selectArticleState } from '../../../common/selectors/helpCenter';

declare global {
  interface Window {
    YT: any;
  }
}

function extractVideoIdFromUrl(url: string | null): string | null {
  if (!url) {
    return null;
  }

  const videoIdPattern =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|watch)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\r\n]{11})/;

  const match = url.match(videoIdPattern);
  return match ? match[1] : null;
}

export function useYouTubeVideoTracker(containerRef: RefObject<HTMLDivElement>, articleId: string): void {
  const store = useStore();
  const { isLoading } = useSelector(selectArticleState);
  const playersRef = useRef<any[]>([]);
  const viewedVideosRef = useRef<Set<string>>(new Set());

  const handleVideoPlay = useCallback(
    (videoUrl: string) => {
      const state = store.getState();
      logArticleVideoPlay(state, { videoUrl });
    },
    [store],
  );

  const handleVideoView = useCallback(
    async (videoId: string) => {
      if (viewedVideosRef.current.has(videoId)) {
        return;
      }

      viewedVideosRef.current.add(videoId);

      const state = store.getState();
      const sharedBiFields = await getSharedBiFieldsFromState(state);
      const presentedContent = createArticleSolutionInfo({ articleId });

      await biLogger.helpWidgetItemOnViewNew(state, {
        widget_language: sharedBiFields.widget_language,
        self_service_session_id: sharedBiFields.self_service_session_id,
        widget_page_type: 'article',
        presented_content: JSON.stringify(presentedContent),
        item_name: `article_video`,
      });
    },
    [articleId, store],
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const containerElement = containerRef.current;
    const viewedVideos = viewedVideosRef.current;

    if (!containerElement) {
      return;
    }

    const videoIframes = containerElement.querySelectorAll('.component.video-component iframe');

    if (videoIframes.length === 0) {
      return;
    }

    const videoData: { id: string; src: string; videoId: string }[] = [];

    videoIframes.forEach((iframe, index) => {
      const src = iframe.getAttribute('src');
      if (src) {
        const videoId = extractVideoIdFromUrl(src);
        if (videoId) {
          const uniqueId = `yt-video-${videoId}-${index}`;
          iframe.setAttribute('id', uniqueId);
          videoData.push({ id: uniqueId, src, videoId });
        }
      }
    });

    if (videoData.length === 0) {
      return;
    }

    // Set up visibility tracking
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          const { target, isIntersecting } = entry;
          const id = target.getAttribute('id');
          const videoDataItem = videoData.find((item) => item.id === id);

          if (id && videoDataItem && isIntersecting) {
            await handleVideoView(videoDataItem.videoId);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      },
    );

    videoIframes.forEach((iframe) => {
      observer.observe(iframe);
    });

    const loadYouTubeAPI = () => {
      if (document.getElementById('youtube-api-script')) {
        return;
      }

      const tag = document.createElement('script');
      tag.id = 'youtube-api-script';
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }

      tag.onload = () => {
        if (window.YT) {
          window.YT.ready(() => setupVideoTracking());
        }
      };
    };

    const setupVideoTracking = () => {
      videoData.forEach(({ id, src }) => {
        if (window.YT) {
          const player = new window.YT.Player(id, {
            events: {
              onStateChange: (event: any) => {
                if (event.data === 1) {
                  handleVideoPlay(src);
                }
              },
            },
          });
          playersRef.current.push(player);
        }
      });
    };

    if (window.YT) {
      window.YT.ready(() => setupVideoTracking());
    } else {
      loadYouTubeAPI();
    }

    return () => {
      observer.disconnect();

      playersRef.current.forEach((player) => {
        if (player && typeof player.destroy === 'function') {
          player.destroy();
        }
      });
      playersRef.current = [];
      viewedVideos.clear();
    };
  }, [containerRef, articleId, handleVideoPlay, handleVideoView, isLoading]);
}
